/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/EmailAllocationRailFrame.tsx
 * FILE_ID: EMAILHUB-ALLOCATION-FRAME-001
 * PURPOSE: Thin frame assembling owner, due-bucket, and watcher controls.
 * LANDMINE: Inline metadata must not create its own scroll axis. Its three
 *           labelled cells share the available width and wrap internally;
 *           Due uses one compact overlay trigger instead of four pills.
 */
import type { ReactNode } from 'react';

import { cn } from '../../../_internal/lib/utils';
import { AssignmentControl } from './AssignmentControl';
import { DueBucketControl } from './DueBucketControl';
import type { EmailAllocationRailProps, EmailAllocationRailViewModel } from './types';
import { useEmailAllocationRail } from './useEmailAllocationRail';
import { WatchersControl } from './WatchersControl';

/** One labelled workflow cell — exported for the real-control Storybook fixture. */
export function InlineWorkflowCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      data-workflow-cell={label.toLowerCase()}
      className="flex min-w-0 flex-col items-start gap-1 px-2.5 py-1.5"
    >
      <span
        aria-hidden
        className="shrink-0 select-none text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
      >
        {label}
      </span>
      <span className="sr-only">{label}</span>
      <div className="flex min-w-0 w-full flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

/** Pure inline workflow layout used by the live frame and visual fixtures. */
export function InlineEmailWorkflow({ vm }: { vm: EmailAllocationRailViewModel }) {
  const scheduledFor = vm.dueBucket.scheduledForLabel ? (
    <span
      className="truncate text-[10.5px] text-muted-foreground"
      data-testid="email-allocation-scheduled-for"
      title="Concrete date the assignee should action by"
    >
      → {vm.dueBucket.scheduledForLabel}
    </span>
  ) : null;

  return (
    <div
      className={cn(
        'grid min-w-0 w-full grid-cols-3 items-stretch',
        'divide-x divide-border/40 overflow-visible'
      )}
      data-testid="email-allocation-rail"
      data-allocation-variant="inline"
    >
      <InlineWorkflowCell label="Owner">
        <AssignmentControl vm={vm.assignment} showLabel={false} />
      </InlineWorkflowCell>
      <InlineWorkflowCell label="Due">
        <DueBucketControl vm={vm.dueBucket} compact />
        {scheduledFor}
      </InlineWorkflowCell>
      <InlineWorkflowCell label="Watchers">
        <WatchersControl vm={vm.watchers} inline showLabel={false} />
      </InlineWorkflowCell>
    </div>
  );
}

export function EmailAllocationRail(props: EmailAllocationRailProps) {
  const vm = useEmailAllocationRail(props);
  const inline = props.variant === 'inline';

  const scheduledFor = vm.dueBucket.scheduledForLabel ? (
    <span
      className="text-[10.5px] text-muted-foreground"
      data-testid="email-allocation-scheduled-for"
      title="Concrete date the assignee should action by"
    >
      → {vm.dueBucket.scheduledForLabel}
    </span>
  ) : null;

  if (inline) {
    return <InlineEmailWorkflow vm={vm} />;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-x-3 gap-y-2',
        'mb-4 flex-wrap rounded-xl border border-border bg-gradient-to-b from-muted/40 to-muted/10 p-3'
      )}
      data-testid="email-allocation-rail"
      data-allocation-variant={props.variant ?? 'card'}
    >
      <AssignmentControl vm={vm.assignment} />
      <DueBucketControl vm={vm.dueBucket} />
      {scheduledFor}
      <WatchersControl vm={vm.watchers} inline={inline} />
    </div>
  );
}
